/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';
import type React from 'react';

import { InformationCircleIconOutline } from '../../icons/generated/heroIcons/InformationCircleIconOutline';
import { Tooltip } from '../../tooltip';
import { Typography } from '../../typography';
import { type TooltipObjectProps } from '../types';
import { type FormFieldSize, type FormFieldState } from './form-control-types';

type FormFieldLabelProps = {
  field: FormFieldState;
  size: FormFieldSize;
  /**
   * How the label is associated with its control. `explicit` renders a
   * `<label>` pointing at the control through `htmlFor`, carrying `labelId`
   * for `aria-labelledby`. `implicit` renders plain text, for components
   * whose control is wrapped by an enclosing `<label>`, where a nested label
   * element would be invalid.
   */
  association?: 'explicit' | 'implicit';
  /**
   * Click handler for an explicit label, for controls that need more than
   * the default `htmlFor` focus forwarding (the Combobox label must move
   * focus without toggling the panel).
   */
  onLabelClick?: React.MouseEventHandler<HTMLLabelElement>;
  /** Text displayed in an information tooltip next to the label */
  moreInformationText?: React.ReactNode;
  /** Props for the Tooltip component used by the information icon */
  tooltipProps?: TooltipObjectProps;
  /** Extra class names for the label row */
  className?: string;
  /** Wraps the rendered row, so a component can put it inside a Skeleton. */
  wrap?: (row: React.ReactNode) => React.ReactNode;
};

/**
 * The label row of a form field: the label itself, an optional information
 * tooltip, and the Required/Optional indicator. The indicator and the tooltip
 * are separate elements rather than label content, keeping them out of the
 * control's accessible name.
 */
export const FormFieldLabel = ({
  field,
  size,
  association = 'explicit',
  onLabelClick,
  moreInformationText,
  tooltipProps,
  className,
  wrap,
}: FormFieldLabelProps) => {
  const {
    label,
    labelId,
    controlId,
    hasVisuallyHiddenLabel,
    hasRequiredIndicator,
    isRequired,
  } = field;
  const variant = size === 'large' ? 'body-large' : 'body-medium';
  const isExplicit = association === 'explicit';
  const informationIconClasses = classNames({
    'ndl-information-icon-large': size === 'large',
    'ndl-information-icon-small': size === 'small' || size === 'medium',
  });

  const row = (
    <div
      className={classNames(
        'ndl-form-label-row',
        { 'ndl-visually-hidden': hasVisuallyHiddenLabel },
        className,
      )}
    >
      <Typography
        as={isExplicit ? 'label' : 'span'}
        variant={variant}
        className="ndl-form-label"
        htmlAttributes={
          isExplicit
            ? { htmlFor: controlId, id: labelId, onClick: onLabelClick }
            : undefined
        }
      >
        {label}
      </Typography>
      {Boolean(moreInformationText) && (
        <Tooltip type="simple" {...tooltipProps?.root}>
          <Tooltip.Trigger
            {...tooltipProps?.trigger}
            className={informationIconClasses}
            hasButtonWrapper
          >
            <div
              tabIndex={0}
              role="button"
              aria-label="Information icon"
              data-testid="ndl-information-icon"
            >
              <InformationCircleIconOutline />
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content {...tooltipProps?.content}>
            {moreInformationText}
          </Tooltip.Content>
        </Tooltip>
      )}
      {hasRequiredIndicator && (
        <Typography variant={variant} className="ndl-form-label-indicator">
          {isRequired ? 'Required' : 'Optional'}
        </Typography>
      )}
    </div>
  );

  return wrap === undefined ? row : <>{wrap(row)}</>;
};
